import classNames from 'classnames';
import React from 'react';

import { Pagination } from '@buildoutinc/blueprint-react/ui/Pagination';

interface Props {
  className?: string;
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
}

type PageSlot = number | 'separator';

const SIBLINGS = 1;
const MAX_SLOTS = SIBLINGS * 2 + 5;

function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function pageSlots(currentPage: number, totalPages: number): PageSlot[] {
  if (totalPages <= MAX_SLOTS) return range(1, totalPages);

  const leftGap = currentPage - SIBLINGS > 2;
  const rightGap = currentPage + SIBLINGS < totalPages - 1;

  if (!leftGap && rightGap) return [...range(1, MAX_SLOTS - 2), 'separator', totalPages];
  if (leftGap && !rightGap) return [1, 'separator', ...range(totalPages - (MAX_SLOTS - 3), totalPages)];

  return [1, 'separator', ...range(currentPage - SIBLINGS, currentPage + SIBLINGS), 'separator', totalPages];
}

export default function PaginationBlueprint({ className, currentPage, totalPages, onChange }: Props) {
  if (!totalPages || totalPages <= 1) return null;

  // Prevent out of bounds page index when fetching new data
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const isFirst = safeCurrentPage === 1;
  const isLast = safeCurrentPage === totalPages;

  function goTo(e: React.MouseEvent<HTMLAnchorElement>, page: number) {
    e.preventDefault();

    if (page === safeCurrentPage || page < 1 || page > totalPages) return;

    onChange(page);
  }

  return (
    <Pagination className={classNames('d-flex justify-content-center', className)}>
      <Pagination.Content>
        <Pagination.Item>
          <Pagination.Previous
            aria-disabled={isFirst}
            className={classNames({ disabled: isFirst })}
            href="#"
            tabIndex={isFirst ? -1 : undefined}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => goTo(e, safeCurrentPage - 1)}
          />
        </Pagination.Item>

        {pageSlots(safeCurrentPage, totalPages).map((slot, index) => (
          slot === 'separator' ? (
            <Pagination.Item key={`separator-${index}`}>
              <Pagination.Separator />
            </Pagination.Item>
          ) : (
            <Pagination.Item key={slot}>
              <Pagination.Link
                href="#"
                isActive={slot === safeCurrentPage}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => goTo(e, slot)}
              >
                {slot}
              </Pagination.Link>
            </Pagination.Item>
          )
        ))}

        <Pagination.Item>
          <Pagination.Next
            aria-disabled={isLast}
            className={classNames({ disabled: isLast })}
            href="#"
            tabIndex={isLast ? -1 : undefined}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => goTo(e, safeCurrentPage + 1)}
          />
        </Pagination.Item>
      </Pagination.Content>
    </Pagination>
  );
}
